/**
 * sb-pairing.js — SoundBird pairing guide behaviour
 * ---------------------------------------------------------------------------
 * STATE IS ONE INTEGER: the index of the open step. The rail, the progress bar
 * and the rows all WRITE to it and all READ from it, and there is no per-step
 * open/closed flag anywhere — which is what stops the three of them drifting
 * apart. Everything below is `setStep` plus the things that call it.
 *
 * The finished state is index === total, one PAST the last step, rather than a
 * wrap to zero. Its card is in the markup like any other row, so an index that
 * lands out of range renders it instead of throwing.
 *
 * JUMPING IS UNRESTRICTED, forwards and backwards, from the rail, the bar or
 * any row. Someone who misread a light four steps ago has to get back to it
 * without redoing the flow, so nothing here gates a step on the one before it.
 *
 * Vanilla, no dependencies (theme rule).
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-sb-pair]');
  if (!root) return;

  const total = parseInt(root.dataset.total, 10) || 0;
  if (!total) return;

  const support = root.dataset.support || '';

  const rows   = Array.from(root.querySelectorAll('[data-step]'));
  const segs   = Array.from(root.querySelectorAll('.sb-pair__seg'));
  const rails  = Array.from(root.querySelectorAll('[data-rail]'));
  const stack  = root.querySelector('[data-sb-stack]');
  const fill   = root.querySelector('[data-sb-fill]');
  const count  = root.querySelector('[data-sb-count]');
  const mBack  = root.querySelector('.sb-pair__mback');
  const mirror = root.querySelector('[data-sb-mirror-label]');

  const mobile = () => matchMedia('(max-width: 749px)').matches;

  /* ── The URL is the source of truth on load ─────────────────────────
     Query string rather than a hash: the theme already uses hashes for
     in-page anchors (the policy pages' contents rail), and a hash here would
     be caught by that machinery. Deep-linking matters because support needs
     to send someone straight to one step. */
  const fromUrl = () => {
    const raw = new URLSearchParams(location.search).get('step');
    const n = parseInt(raw, 10);
    /* ?step=4 is human-facing, so it is 1-based; the index is not. */
    return Number.isFinite(n) ? clamp(n - 1) : 0;
  };

  function clamp(i) {
    if (!Number.isFinite(i) || i < 0) return 0;
    return i > total ? total : i;
  }

  let current = -1;

  function setStep(next, opts) {
    const i = clamp(next);
    if (i === current) return;
    current = i;

    rows.forEach((row) => {
      const n = parseInt(row.dataset.step, 10);
      row.classList.toggle('is-done', n < i);
      row.classList.toggle('is-open', n === i);
      row.classList.toggle('is-next', n > i);
    });

    segs.forEach((seg, n) => {
      seg.classList.toggle('is-done', n < i);
      seg.classList.toggle('is-now', n === i);
    });

    rails.forEach((item, n) => {
      item.classList.toggle('is-done', n < i);
      item.classList.toggle('is-now', n === i);
      const btn = item.querySelector('.sb-pair__railBtn');
      if (btn) {
        if (n === i) btn.setAttribute('aria-current', 'step');
        else btn.removeAttribute('aria-current');
      }
    });

    /* The mobile footer is a second copy of the two controls, so its label is
       mirrored from whichever card is open. The wording rules stay in Liquid
       (snippets/sb-pairing-actions.liquid) and are never restated here. */
    if (mirror) {
      const open = rows[Math.min(i, rows.length - 1)];
      const src = open && open.querySelector('[data-sb-next] span');
      if (src) mirror.textContent = src.textContent;
      const arrow = mirror.parentElement.querySelector('svg');
      /* "Start again" restarts rather than advances, so the arrow would lie. */
      if (arrow) arrow.style.display = i >= total ? 'none' : '';
    }

    /* The finished state fills the line completely rather than sitting one
       segment short of it. */
    if (fill) fill.style.inlineSize = Math.round((i / total) * 100) + '%';
    if (count) count.textContent = (i >= total ? total : i + 1) + '/' + total;

    /* The back control stays on step 1 rather than going hidden (owner,
       2026-08-15) — there it leaves the guide for support instead of stepping
       backwards. Keeping it present means the row above the list never
       reflows, and a customer who opened the wrong guide has a way out that
       is not the browser's own back button. */
    if (mBack) {
      const home = i === 0;
      mBack.setAttribute('aria-label', home ? 'Back to support' : 'Previous step');
    }

    const url = new URL(location.href);
    if (i === 0) url.searchParams.delete('step');
    else url.searchParams.set('step', String(i + 1));
    history.replaceState(null, '', url);

    if (mobile()) keepInView();
    if (opts && opts.focus) focusOpen();
  }

  /* ── Keep the open step in view (mobile) ────────────────────────────
     Every completed step adds a row ABOVE the open card, so without this the
     active step walks off the bottom of the scroller as you advance.
     scrollTop rather than scrollIntoView, deliberately: scrollIntoView also
     scrolls every ancestor, which on this page drags the whole document and
     the header pill around with it. */
  function keepInView() {
    if (!stack) return;
    const open = rows[Math.min(current, rows.length - 1)];
    if (!open) return;
    const card = open.querySelector('.sb-pair__open') || open;
    const top = card.getBoundingClientRect().top - stack.getBoundingClientRect().top;
    stack.scrollTop += top - 16;
    paintFades();
  }

  function focusOpen() {
    const open = rows[Math.min(current, rows.length - 1)];
    const title = open && open.querySelector('.sb-pair__stepTitle');
    if (!title) return;
    /* Programmatic focus so a keyboard user is not dropped back at the top of
       the stack after every advance. Removed again on blur so it never becomes
       a tab stop of its own. */
    title.setAttribute('tabindex', '-1');
    title.focus({ preventScroll: true });
    title.addEventListener('blur', () => title.removeAttribute('tabindex'), { once: true });
  }

  /* ── Edge fades ─────────────────────────────────────────────────────
     Reworked from an overlay to a mask (owner, 2026-08-15: the overlay
     looked bad once the grouped-tick box gave completed rows their own
     translucent fill — the overlay's "opaque" stop was plain field colour,
     a visibly different shade from white-over-field, so the fade read as a
     patch of the wrong colour rather than a fade of the row under it).

     A CSS mask fades the row's OWN rendered pixels instead of painting a
     guessed colour on top, so it is correct under any fill without needing
     to know what that fill is. The gradients themselves live in the CSS
     (`.sb-pair__stack[data-fade-top]` etc.) — this only decides which of the
     two attributes apply, from scroll position. Presence, not opacity: a
     partial mask is exactly the "gap that never reaches opaque" bug from the
     overlay version, so this writes a plain boolean, never a fraction. */
  function paintFades() {
    if (!stack) return;
    const y = stack.scrollTop;
    const max = stack.scrollHeight - stack.clientHeight;
    stack.toggleAttribute('data-fade-top', y > 0);
    stack.toggleAttribute('data-fade-bottom', max - y > 1);
  }

  if (stack) stack.addEventListener('scroll', paintFades, { passive: true });

  /* ── Wiring ─────────────────────────────────────────────────────────
     Delegated, so the handlers survive the section being re-rendered by the
     theme editor without needing to be rebound. */
  root.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-jump]');
    if (jump && root.contains(jump)) {
      setStep(parseInt(jump.dataset.jump, 10), { focus: true });
      return;
    }

    if (e.target.closest('[data-sb-next]')) {
      /* Past the last step this is "Start again", which returns to the top
         rather than advancing into nothing. */
      setStep(current >= total ? 0 : current + 1, { focus: true });
      return;
    }

    if (e.target.closest('[data-sb-back]')) {
      /* On step 1 there is nowhere back to go inside the guide, so it leaves
         for support. Only the phone control is on screen there — the card's
         own Back is not rendered on the first step. */
      if (current === 0) {
        if (support) location.href = support;
        return;
      }
      setStep(current - 1, { focus: true });
    }
  });

  /* Re-seat the open card when the layout changes under it — a rotation or a
     desktop/mobile crossing invalidates the scroll correction. */
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (mobile()) keepInView();
      paintFades();
    }, 120);
  }, { passive: true });

  /* Back/forward between deep-linked steps. */
  window.addEventListener('popstate', () => setStep(fromUrl()));

  setStep(fromUrl());
  paintFades();
})();
